import { Routes, Route, Navigate } from 'react-router-dom';
import { LEGACY_TEMPO_REDIRECTS } from './legacy-redirects';
// FRONTEND GOVERNANCE (.claude/rules/ui-package.md): the unreadable-entitlement
// notice below composes the shared primitives rather than re-implementing an
// alert box out of raw divs and a raw button.
import { Alert, Button } from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';
import { useInstitutionFlags } from '../hooks/useInstitutionFlags';
import { hasPeRole } from '../lib/pe-roles';
import { PortalLayout } from '../layouts/PortalLayout';
import { LoginPage } from '../pages/Login';
import { OAuthCallbackPage } from '../pages/OAuthCallback';
import { SSOCallbackPage } from '../pages/SSOCallback';
import { StaffLaunchPage } from '../pages/StaffLaunch';
import { StaffLoginPage } from '../pages/StaffLogin';
import { AcceptInvitationPage } from '../pages/AcceptInvitation';
import { DashboardPage } from '../pages/Dashboard';
import { MemberLookupPage } from '../pages/MemberLookup';
import { ComplianceReportsPage } from '../pages/ComplianceReports';
import { ActiveLoansPage } from '../pages/ActiveLoans';
import { CollateralPolicyPage } from '../pages/collateral-policy';
import { AccountDecisionPage, CoveragePolicyPage, PoolAppetitePage } from '../pages/tempo';
import { CoreDepositFeedPage, FeedDeliveryDetailPage } from '../pages/core-deposit-feed';
import { EconomicModelingScenariosPage } from '../pages/economic-modeling';
import { ProfilePage } from '../pages/Profile';
import { NotFoundPage } from '../pages/NotFound';
import { PortfolioPage } from '../pages/Portfolio';
import { LoanPipelineDashboard } from '../pages/dashboards/LoanPipeline';
import { CollateralMonitoringDashboard } from '../pages/dashboards/CollateralMonitoring';
import { ComplianceStatusDashboard } from '../pages/dashboards/ComplianceStatus';
import { RiskDashboard } from '../pages/dashboards/RiskDashboard';
// Platform-scoped pages relocated to apps/platform-admin/ on 2026-05-17
// (F-PORTAL-AUDIENCE-SPLIT). Per docs/architecture/identity-domains.md
// "Two-Portal Architecture", admin-portal is the FI-tenant operator scope
// (port 9300) and platform-admin is the Aaim platform-operator scope
// (port 9303). Tenancy roster, system health, audit logs, lending defaults,
// platform configuration sub-pages, notification routing, and the
// All-Institutions roster are now served exclusively from platform-admin.
//   - Canonical platform-admin already had: Tenants, AuditLogs, SystemHealth,
//     Settings, NotificationRouting -- duplicate admin-portal copies deleted.
//   - Moved into platform-admin: AllInstitutions (-> /institutions),
//     LendingConfig + PlatformConfig + 9 config sub-pages
//     (-> /platform-config/*), and the providers/ panel registry.
// No back-compat redirect: pre-customer atomic transition per
// .claude/rules/no-pre-customer-tech-debt.md. Direct-URL access to legacy
// /admin/tenants etc. paths in admin-portal falls through to NotFoundPage.
import {
  SignaturesPage,
  UCCFilingsPage,
  PropertyValuationsPage,
  TitleSearchesPage,
  LienVerificationsPage,
  PropertyProfilePage,
} from '../pages/collateral';
import {
  FundAdministrationPage,
  PeCollateralMonitoringPage,
  PeConfigurationPage,
} from '../pages/pe';
// Sprint C17+1 (2026-05-04) removed IntegrationsPage; GH #1729 deliverable 2
// removed IntegrationDetailPage (2026-09-15). Both duplicated platform-admin's
// Integrations surface and were tied to the deprecated PlatformDataProvider
// model. No integrations page or route remains in admin-portal.
import {
  PledgeInboxPage,
  ActivePledgesPage,
  PledgeDetailPage,
} from '../pages/pledges';
import { AlertsPage } from '../pages/Alerts';
import { NotificationsPage } from '../pages/Notifications';
import { TasksPage } from '../pages/Tasks';
// NotificationRoutingPage was imported here through 2026-05-17 -- removed
// per F-PORTAL-AUDIENCE-SPLIT (platform-admin owns notification routing).
import { ExaminersPage } from '../pages/admin/Examiners';
// Admin Wave 2: API keys, user management, identity provider
import { ApiKeysPage } from '../pages/admin/ApiKeys';
import { UserManagementPage } from '../pages/admin/UserManagement';
import { StaffDetailPage } from '../pages/admin/StaffDetail';
// Sprint 5 S5-C: IdP claim queue + role mapping editor
import { PendingIdpClaimsPage } from '../pages/admin/PendingIdpClaims';
import { IdpRoleMappingsPage } from '../pages/admin/IdpRoleMappings';
// Sprint 5 S5-D: ABAC policy authoring + role attachment
import { AbacPoliciesPage } from '../pages/admin/AbacPolicies';
// Sprint C14 Lane B WP-8: tenant-scoped Configuration sub-pages
import {
  OrganizationProfilePage,
  WebhookEndpointsPage,
  CustodianConnectionsPage,
  ConsumerPortalBrandingPage,
  LandingPageCopyPage,
  WorkflowOrderingPage,
  SlaSettingsPage,
  LendingConfigurationPage,
} from '../pages/config';
// Sprint C14 Lane B WP B-4 + B-5: Webhook Delivery Log + Custodian Status
import { WebhookDeliveryLogPage } from '../pages/WebhookDeliveryLog';
import { CustodianStatusPage } from '../pages/CustodianStatus';
// Sprint C14 Lane B WP B-6 + B-7: Loan Participations surfaces
import {
  Marketplace,
  ListALoan,
  CompareDeals,
  Commitments,
  Positions,
  PositionDetail,
  OfferingDetail,
  RiskCapital,
  AllocationsReview,
  Indications,
} from '../pages/syndication';
import { PledgeAuditTrailPage } from '../pages/audit/PledgeAuditTrail';
import { RegulatoryReadinessPage } from '../pages/audit/RegulatoryReadiness';
// Examiner evidence package preview + ZIP download (GH #1930)
import { ExaminerEvidencePage } from '../pages/audit/ExaminerEvidence';
// Lien management pages -- awaiting-review (Sprint B Lane B2 default leaf),
// filing status, jurisdiction reports, fee reconciliation
import {
  FilingsAwaitingReview,
  FilingStatus,
  JurisdictionReports,
  FeeReconciliation,
} from '../pages/lien';
// Wave 2: Monitoring triad -- asset classification, valuation history, concentration risk
import {
  AssetClassificationPage,
  ValuationHistoryPage,
  ConcentrationRiskPage,
} from '../pages/monitoring';
// W2d arc-followon-1 S5: Support page tree (FI-operator self-cases; the
// operator queue, SLA rules and agent roster moved to platform-admin, GH #678)
import {
  SupportCasesPage,
  SupportCaseDetailPage,
  SupportNewCasePage,
} from '../pages/Support';

/**
 * Protected route wrapper
 * Redirects to login if not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

/**
 * Decode a JWT's `exp` (seconds) without verifying the signature -- purely to
 * detect a client-side-expired access token so the route guard can redirect to
 * re-auth BEFORE page components fire queries that would 401 piecemeal. Returns
 * true when the token is present AND past expiry; false when absent or invalid
 * (absent/opaque tokens are left to the API-layer 401 + silent-refresh path).
 */
function isAccessTokenExpired(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    if (typeof payload.exp !== 'number') return false;
    // 30s skew so a token about to expire is treated as expired (avoids a
    // request that lands just after the boundary).
    return Date.now() >= (payload.exp - 30) * 1000;
  } catch {
    return false;
  }
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, accessToken, refreshToken } = useAuthStore();

  // An access token that is present but expired -> redirect to re-auth UNLESS
  // a refresh token exists (the API client's silent-refresh will renew it on
  // the next request). This is the session guard: it fires before child pages
  // mount their queries, so an expired session produces ONE clean redirect
  // instead of every widget failing with a 401. It runs even under
  // BYPASS_AUTH, because dev-bypass does not cover the aaim-core-proxied
  // surfaces (FI Staff / IdP / ABAC) -- those 401 for real on an expired token.
  if (isAccessTokenExpired(accessToken) && !refreshToken) {
    return <Navigate to="/login" replace />;
  }

  if (BYPASS_AUTH) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * PE-Valuation feature-flag gate (F-AP-PE-PARSE-001).
 *
 * The PE group is OFF by default for community-bank / CU profiles. Tenants
 * that explicitly enable `enablePeValuation` see the routes; everyone else
 * is redirected to the dashboard. This prevents the "<!doctype" JSON parse
 * error caused by the page calling a PE endpoint that is not provisioned
 * for the tenant.
 *
 * THREE states, not two (GH #2742 class). The entitlement read used to degrade
 * to all-false, so an unreadable flag was indistinguishable from a withheld
 * one and an ENTITLED operator was silently redirected off /pe/* -- landing on
 * the dashboard with nothing saying why, and no reason to suspect a failed read.
 *
 * The disposition on a failed read is BLOCK WITH A NOTICE, not permissive
 * default. Permissive default would mount the PE pages, which immediately call
 * PE endpoints that are not provisioned when the tenant is not entitled -- the
 * exact "<!doctype" JSON parse error F-AP-PE-PARSE-001 created this gate to
 * prevent. Rendering the surface on an unreadable flag therefore re-opens the
 * original defect. The notice keeps the operator on the URL they asked for,
 * names the failure, and offers a retry; it asserts neither entitlement nor
 * withholding, which is the only honest thing to say when the read failed.
 * (This is the file's only flag-gated route, so there is no sibling precedent
 * to follow -- the reasoning is stated here rather than inherited.)
 *
 * ROLE (GH #4228). pe-valuation runs `requireUserRole(PE_USER_ROLES)` after its
 * entitlement gate (GH #2657), so a role outside that list gets 403 on every PE
 * call even for an entitled tenant. Such a caller is redirected to the
 * dashboard, as on a withheld entitlement, before the flags are consulted: the
 * pages could only fail for it. The list is `lib/pe-roles.ts`, the same one
 * the three PE nav items carry as `requiredRole`.
 */
interface PeFeatureGateProps {
  children: React.ReactNode;
}

// Exported so the three-state behavior is testable without mounting the whole
// route tree (and every page module behind it).
export function PeFeatureGate({ children }: PeFeatureGateProps) {
  const roles = useAuthStore((s) => s.user?.roles);
  const { flagsRead, flagsLoading, flagsUnreadable, flagsError, refetch } =
    useInstitutionFlags();

  if (!hasPeRole(roles)) {
    return <Navigate to="/" replace />;
  }

  if (flagsLoading) {
    return null;
  }

  if (flagsUnreadable || flagsRead === null) {
    return (
      <div className="p-6" role="alert">
        <Alert type="error" title="PE Valuation availability could not be determined">
          <div className="space-y-2">
            <div>
              {flagsError instanceof Error
                ? flagsError.message
                : 'Entitlements could not be read.'}
            </div>
            <div>
              This does not mean your institution lacks the entitlement -- the
              read itself failed.
            </div>
            <Button
              variant="tertiary"
              size="small"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!flagsRead.enablePeValuation) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Main application routes
 *
 * Route structure:
 * - /login - Public login page
 * - / - Dashboard (protected)
 * - /dashboards/loan-pipeline - Loan pipeline dashboard (protected)
 * - /dashboards/collateral-monitoring - redirects to /monitoring (Health Overview)
 * - /dashboards/compliance-status - Compliance status dashboard (protected)
 * - /dashboards/system-health - System health dashboard (protected)
 * - /dashboards/risk - Risk management dashboard (protected)
 * - /loans/active - Active loans table (protected)
 * - /pe/funds - PE fund administration (protected)
 * - /pe/collateral-monitoring - PE collateral monitoring (protected)
 * - /pe/configuration - PE configuration reference (protected)
 * - /settings - Global settings (protected, admin only)
 * - /auth/sso/callback - CU*Answers SSO callback (public)
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<OAuthCallbackPage />} />
      <Route path="/auth/sso/callback" element={<SSOCallbackPage />} />
      <Route path="/launch" element={<StaffLaunchPage />} />
      {/* MX-Auth UPSTREAM Connect widget (mxauth-06, #1807). Anonymous; the
          ?inst=<quilttInstitutionId>&email=&token=<invitationToken> link comes
          from the staff invitation. Runs Quiltt Connect -> consumer-api
          connect-result -> /launch?code=<handoffCode> (StaffLaunch is its
          terminal consumer). */}
      <Route path="/staff/login" element={<StaffLoginPage />} />
      {/* ADR-008 pilot-operator invitation accept (credential path). Anonymous;
          the URL ?token=<plaintext> is the auth proof. Submits to
          POST /api/v1/auth/invitations/accept via useAcceptInvitation. */}
      <Route path="/invitations/accept" element={<AcceptInvitationPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        {/* Dashboards */}
        <Route path="dashboards/loan-pipeline" element={<LoanPipelineDashboard />} />
        {/* One page, one title (r3-fi-cons-11): this path mounted the same
            component as /monitoring under a second title. Kept as a redirect
            so a bookmark still lands on the Health Overview. */}
        <Route path="dashboards/collateral-monitoring" element={<Navigate to="/monitoring" replace />} />
        <Route path="dashboards/compliance-status" element={<ComplianceStatusDashboard />} />
        <Route path="dashboards/risk" element={<RiskDashboard />} />
        {/* Loans & Portfolio */}
        <Route path="loans/active" element={<ActiveLoansPage />} />
        <Route path="portfolio" element={<PortfolioPage />} />
        {/* Collateral - Contract Documentation */}
        <Route path="collateral/signatures" element={<SignaturesPage />} />
        <Route path="collateral/ucc-filings" element={<UCCFilingsPage />} />
        <Route path="collateral/property-valuations" element={<PropertyValuationsPage />} />
        <Route path="collateral/title-searches" element={<TitleSearchesPage />} />
        <Route path="collateral/lien-verifications" element={<LienVerificationsPage />} />
        <Route path="collateral/property-profile" element={<PropertyProfilePage />} />
        {/* PE Valuation -- gated behind Institution.enablePeValuation
            (F-AP-PE-PARSE-001). Default-OFF; tenants without the flag
            redirect to the dashboard rather than render the page and
            invoke a PE endpoint that returns HTML on 404. */}
        <Route
          path="pe/funds"
          element={<PeFeatureGate><FundAdministrationPage /></PeFeatureGate>}
        />
        <Route
          path="pe/collateral-monitoring"
          element={<PeFeatureGate><PeCollateralMonitoringPage /></PeFeatureGate>}
        />
        <Route
          path="pe/configuration"
          element={<PeFeatureGate><PeConfigurationPage /></PeFeatureGate>}
        />
        {/* Pledge Review (Sprint C14 Lane B WP B-1, B-2) */}
        {/*
          Sprint C14: PledgeQueue.tsx renamed to PledgeInbox.tsx in-place via
          `git mv`. The /pledges route stays as the operator inbox; the new
          /pledges/active route surfaces ACKNOWLEDGED + ACTIVE pledges with
          a CHA tier rollup column. /pledges/queue is preserved as a redirect
          to /pledges for back-compat with any deep links operators may have
          bookmarked from the pre-rename portal.
        */}
        <Route path="pledges" element={<PledgeInboxPage />} />
        <Route path="pledges/active" element={<ActivePledgesPage />} />
        <Route path="pledges/queue" element={<Navigate to="/pledges" replace />} />
        <Route path="pledges/:pledgeId" element={<PledgeDetailPage />} />
        {/* Sprint C14 Lane B WP B-3: Alerts queue */}
        <Route path="alerts" element={<AlertsPage />} />
        {/* GH #2281: FI notification inbox (the bell's View-all destination) */}
        <Route path="notifications" element={<NotificationsPage />} />
        {/* FI Task Queue (Sprint 171 D-002) */}
        <Route path="tasks" element={<TasksPage />} />
        {/* Members & Compliance */}
        <Route path="members" element={<MemberLookupPage />} />
        <Route path="compliance" element={<ComplianceReportsPage />} />
        {/* Policy -- collateral policy config (own nav group) */}
        <Route path="policy/*" element={<CollateralPolicyPage />} />
        {/* GH #2748 item 2: /policy/member-liquidity and /policy/pool-appetite
            were the pre-644fbedf9f Tempo paths. That rename moved the consoles
            to /tempo without leaving a redirect, so a bookmarked or shared
            deep link at the old path matched policy/* above (static segments
            outrank the wildcard, but these routes did not exist before this
            fix) and rendered CollateralPolicyPage's inner <Routes> with no
            matching tab -- chrome with no content. Same redirect idiom as
            pledges/queue -> pledges below. The pairs live in
            ./legacy-redirects so the regression test guards this same data. */}
        {LEGACY_TEMPO_REDIRECTS.map(({ from, to }) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        {/* Aaim Tempo policy consoles (overdraft-coverage arc OC-3). Top-level
            /tempo routes, renamed from /policy/member-liquidity to keep the
            retiring "member liquidity" nomenclature off the visible path
            (operator ruling 2026-08-27). Conditional on enableOverdraftPreview
            at the nav level (PortalLayout.buildFiTenantGroups); the SPA does not
            gate the route itself -- a deep link from a tenant without the flag
            hits the tempo-engine's server-side 404 gate (the
            syndication precedent). */}
        <Route path="tempo" element={<CoveragePolicyPage />} />
        <Route path="tempo/pool-appetite" element={<PoolAppetitePage />} />
        {/* mlv2-console D2, the C3 two-path half (sprint-queue/
            sprint-member-liquidity-v2/GO-LIVE-PLAN.md Section 13.2(b)): the
            per-account decision screen. Tempo declares no operation
            that lists accounts, so /tempo/accounts is a lookup field rather
            than a roster and the identifier segment carries the read. Same
            flag posture as the consoles above -- no SPA gate; an unentitled
            tenant hits the tempo-engine's 404. */}
        <Route path="tempo/accounts" element={<AccountDecisionPage />} />
        <Route path="tempo/accounts/:accountId" element={<AccountDecisionPage />} />
        {/* Core deposit feed (GH #2911, LOI section 2.2). Outside /tempo: that
            nav group requires settings:update (FI_ADMIN only), while the
            feed's reads are open to every FEED_READ_ROLES role. No SPA gate;
            the tempo-engine router's role check and its overdraft-preview 404
            decide. */}
        <Route path="core-deposit-feed" element={<CoreDepositFeedPage />} />
        <Route path="core-deposit-feed/deliveries/:deliveryId" element={<FeedDeliveryDetailPage />} />
        {/* FI Economic Modeling (GH #2727). Conditional on
            enableOverdraftPreview at the nav level; the SPA does not gate the
            route itself -- a deep link from an unentitled tenant hits
            requireModelingEntitlement's server-side 403, which the page renders
            as "surface dark" (the member-liquidity precedent above). */}
        <Route path="modeling/scenarios" element={<EconomicModelingScenariosPage />} />
        {/* Sprint C14 Lane B WP-8: tenant-scoped Configuration sub-pages.
            Surfaced under the new "Configuration" nav group alongside
            the existing collateral-policy pages (which keep their own
            /policy/* routes). */}
        <Route path="admin/config/organization" element={<OrganizationProfilePage />} />
        <Route path="admin/config/webhook-endpoints" element={<WebhookEndpointsPage />} />
        <Route path="admin/config/custodian-connections" element={<CustodianConnectionsPage />} />
        <Route path="admin/config/branding" element={<ConsumerPortalBrandingPage />} />
        <Route path="admin/config/landing-copy" element={<LandingPageCopyPage />} />
        {/* Ruling D3 (2026-08-17), reaffirming ruling 3 of 2026-08-13: the FI
            admin selects a pledge workflow ordering from the subset an Aaim
            operator has allowlisted for the tenant. Gated by the FI
            Configuration nav group's settings:update, which is the same
            permission PUT /api/v1/workflow-recipes/tenants/{tenantId}/binding
            requires. Recipe authoring and allowlist EDITING are Aaim-operator
            acts on platform.aaim.com : 9303 and have no route here. */}
        <Route path="admin/config/workflow-ordering" element={<WorkflowOrderingPage />} />
        {/* GH #2390: the FI admin sets the Tasks queue's SLA age thresholds for
            their own institution (GET/PUT /api/v1/admin/tenants/me/sla,
            settings:read / settings:update). Same Configuration nav group. */}
        <Route path="admin/config/sla" element={<SlaSettingsPage />} />
        {/* GH #4688: the institution's own lending configuration (lending-platform
            GET/PUT /api/v1/lending-config). Same Configuration nav group,
            gated on settings:update for fi_admin, and the one read-only item
            of the loan officer's FI Configuration group (Rob's ruling
            2026-10-29 on GH #4688); the save is refused server-side for every
            role but fi_admin and the platform-administrator roles. */}
        <Route path="admin/config/lending" element={<LendingConfigurationPage />} />
        {/* Sprint C14 Lane B WP B-4 + B-5: monitoring surfaces. */}
        <Route path="admin/webhook-deliveries" element={<WebhookDeliveryLogPage />} />
        {/* Canonical custodian-status route; nav (PortalLayout NAV_ID_TO_PATH) points
            to /monitoring/custodian-status. The dead duplicate /admin/custodian-status
            route was removed -- nothing navigated to it (#1729 nav-tree audit). */}
        <Route path="monitoring/custodian-status" element={<CustodianStatusPage />} />
        {/* Wave 2: Monitoring triad -- asset classification, valuation history, concentration risk */}
        <Route path="monitoring/classification" element={<AssetClassificationPage />} />
        <Route path="monitoring/valuation-history" element={<ValuationHistoryPage />} />
        <Route path="monitoring/concentration" element={<ConcentrationRiskPage />} />
        {/* Sprint C14 Lane B WP B-6 + B-7: Loan Participations FI-tenant surfaces.
            Conditional on enableLoanParticipations at the nav level
            (PortalLayout.buildFiTenantGroups) and at the API level
            (services/loan-syndication-protocol/auth role gate). The SPA
            does not gate the route itself -- a deep link from a tenant
            without the flag will hit a 404 from the LSP service. */}
        <Route path="syndication/marketplace" element={<Marketplace />} />
        <Route path="syndication/list-a-loan" element={<ListALoan />} />
        <Route path="syndication/compare" element={<CompareDeals />} />
        <Route path="syndication/offerings/:offeringId" element={<OfferingDetail />} />
        <Route
          path="syndication/offerings/:offeringId/allocations"
          element={<AllocationsReview />}
        />
        <Route path="syndication/indications" element={<Indications />} />
        <Route path="syndication/commitments" element={<Commitments />} />
        <Route path="syndication/positions" element={<Positions />} />
        <Route path="syndication/positions/:positionId" element={<PositionDetail />} />
        <Route path="syndication/risk-capital" element={<RiskCapital />} />
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        {/* rbac-02 (#1285): the legacy role-assignment management page was
            retired. The canonical FI staff lifecycle (UserManagement at
            /admin/users + StaffDetail at /admin/staff/:staffId + the invite
            modal) fully covers its function (roster, multi-role grant, invite,
            suspend/offboard with step-up). No redirect: it shared no stable
            bookmark surface and its nav entries are removed in PortalLayout. */}
        {/* Pledge Audit Trail -- resourceType=pledge scoped audit log */}
        <Route path="audit/pledges" element={<PledgeAuditTrailPage />} />
        {/* Regulatory Readiness -- PAL heat map per entity config (Wave 2) */}
        <Route path="audit/regulatory-readiness" element={<RegulatoryReadinessPage />} />
        {/* Examiner Evidence -- manifest preview + evidence-package ZIP (GH #1930) */}
        <Route path="audit/examiner-evidence" element={<ExaminerEvidencePage />} />
        {/* /monitoring alias -> CollateralMonitoringDashboard (Health Overview nav target) */}
        <Route path="monitoring" element={<CollateralMonitoringDashboard />} />
        {/* Lien management -- Sprint B Lane B2 default leaf is the
            awaiting-review queue; UCC filing status, jurisdiction reports,
            fee reconciliation. The /collateral/ucc-filings route remains the
            "Our Filings" surface for amendments and continuations. */}
        <Route path="lien/awaiting-review" element={<FilingsAwaitingReview />} />
        <Route path="lien/filing-status" element={<FilingStatus />} />
        <Route path="lien/jurisdiction-reports" element={<JurisdictionReports />} />
        <Route path="lien/fee-reconciliation" element={<FeeReconciliation />} />
        {/* Sprint 174 D-003 -- examiner provisioning */}
        <Route path="admin/examiners" element={<ExaminersPage />} />
        {/* Admin Wave 2: API keys, user management, identity provider */}
        <Route path="admin/api-keys" element={<ApiKeysPage />} />
        <Route path="admin/users" element={<UserManagementPage />} />
        {/* Sprint 5 S5-B-pages: per-staff detail page (role bindings + sensitive ops). */}
        <Route path="admin/staff/:staffId" element={<StaffDetailPage />} />
        {/* rbac-02 (#1284): the 9300 global IdP-TYPE registry editor was
            retired. Editing the platform-global identity-provider registry
            (create/edit/delete/suspend) is a 9303 platform-admin authority and
            now 403s for fi_admin (rbac-01). The FI's real IdP work -- review
            pending claims and map IdP roles to Aaim roles -- lives in the KEPT
            pages below. Redirect preserves bookmarks to /admin/identity-provider. */}
        <Route
          path="admin/identity-provider"
          element={<Navigate to="/admin/idp/role-mappings" replace />}
        />
        {/* Sprint 5 S5-C: IdP claim review queue + per-binding role mapping editor */}
        <Route path="admin/idp/pending-claims" element={<PendingIdpClaimsPage />} />
        <Route path="admin/idp/role-mappings" element={<IdpRoleMappingsPage />} />
        {/* Sprint 5 S5-D: ABAC policy authoring + role attachment */}
        <Route path="admin/abac-policies" element={<AbacPoliciesPage />} />
        {/* W2d arc-followon-1 S5: Support page tree. The /admin/support/new
            route MUST be declared BEFORE /admin/support/:caseId so the
            literal segment matches first and is not consumed by the dynamic
            param. GH #678 (operator ruling 2026-10-23) removed /agents and
            /sla: the SLA rule editor and agent roster live in platform-admin
            (platform.aaim.com /support/sla, /support/agents). An old link to
            either now reaches /:caseId, which shows a platform-admin session
            the operator-console pointer card. */}
        <Route path="admin/support" element={<SupportCasesPage />} />
        <Route path="admin/support/new" element={<SupportNewCasePage />} />
        <Route path="admin/support/:caseId" element={<SupportCaseDetailPage />} />
        {/* Economic Modeling moved to strategy-tools app (port 9305) */}
        {/* No /admin/integrations* route. Sprint C17+1 (2026-05-04) removed
            the list page; GH #1729 deliverable 2 removed the detail page and
            this route (2026-09-15). The detail page was a DEAD_PAGE: its only
            entry point was the deleted list, and its own "back" control was a
            <Navigate to="/admin/integrations">, i.e. every exit from it landed
            on NotFoundPage. It also rendered a hardcoded DATA_PROVIDERS map
            (invented lastSync timestamps, record counts and sync histories) and
            a setTimeout "Sync now" that changed nothing --
            .claude/rules/no-fabricated-values.md. platform-admin (9303) owns the
            Integrations surface. */}
        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
